import { Colors } from "@blueprintjs/core";
import { isAfter } from "date-fns";

import participantsIcon from "@/../../hypetrain/static/img/program/participants-icon.svg";
import { pluralize } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { COMPONENT_STATUS_STYLES } from "@/components/pages_logged_in/program/components/constants";
import { ViewComponentButton } from "@/components/pages_logged_in/program/components/ViewComponentButton";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentStatus } from "@/graphql";
import { cn } from "@/lib/utils";
import {
  LeadComponent,
  MainComponent,
  MainProgram,
  ParticipantComponent,
  StatisticComponent,
} from "@/types/programs";

const getStatisticDataFormated = (
  component: StatisticComponent,
  hideStarted = false,
) => {
  const currentTime = new Date();
  const {
    targetDueDate,
    totalParticipants,
    completedCount,
    startedCount,
    status,
  } = component || {};
  const dueDate = targetDueDate ? new Date(targetDueDate) : null;
  const totalParticipantNumber = totalParticipants || totalParticipants || 0;

  let incompleteCount =
    totalParticipantNumber - (completedCount || 0) - (startedCount || 0);

  if (incompleteCount < 0) incompleteCount = 0;

  const completeStyles = COMPONENT_STATUS_STYLES[ComponentStatus.Completed];
  const didNotFinishStyles =
    COMPONENT_STATUS_STYLES[ComponentStatus.DidNotFinish];
  const noResponseStyles = COMPONENT_STATUS_STYLES[ComponentStatus.NoResponse];
  const startedStyles = COMPONENT_STATUS_STYLES[ComponentStatus.Started];
  const pastDueStyles = COMPONENT_STATUS_STYLES[ComponentStatus.PastDue];
  const notStartedStyles = COMPONENT_STATUS_STYLES[ComponentStatus.NotStarted];

  const statisticData = [
    {
      label: completeStyles.text,
      color: completeStyles.color,
      borderColor: completeStyles.borderColor,
      count: completedCount || 0,
    },
  ];

  if (status === ComponentStatus.Completed) {
    if (!hideStarted) {
      statisticData.push({
        label: didNotFinishStyles.text,
        color: didNotFinishStyles.color,
        borderColor: didNotFinishStyles.borderColor,
        count: startedCount || 0,
      });
    }
    statisticData.push({
      label: noResponseStyles.text,
      color: noResponseStyles.color,
      borderColor: noResponseStyles.borderColor,
      count: incompleteCount || 0,
    });
  } else if (status === ComponentStatus.Ongoing) {
    if (dueDate && isAfter(currentTime, dueDate)) {
      if (!hideStarted) {
        statisticData.push({
          label: pastDueStyles.text,
          color: pastDueStyles.color,
          borderColor: pastDueStyles.borderColor,
          count: startedCount || 0,
        });
      }
      statisticData.push({
        label: notStartedStyles.text,
        color: notStartedStyles.color,
        borderColor: notStartedStyles.borderColor,
        count: incompleteCount || 0,
      });
    } else {
      if (!hideStarted) {
        statisticData.push({
          label: startedStyles.text,
          color: startedStyles.color,
          borderColor: startedStyles.borderColor,
          count: startedCount || 0,
        });
      }
      statisticData.push({
        label: notStartedStyles.text,
        color: notStartedStyles.color,
        borderColor: notStartedStyles.borderColor,
        count: incompleteCount || 0,
      });
    }
  }
  return statisticData;
};

interface ParticipantComponentStatisticProps {
  component: (
    | MainComponent
    | ParticipantComponent
    | LeadComponent
    | StatisticComponent
  ) & {
    totalParticipants?: number | null;
  };
  program?: MainProgram;
  isProgramLead?: boolean;
  canShowViewDetailButton?: boolean;
  hideStarted?: boolean;
}

export function ParticipantComponentStatistic({
  component,
  program,
  isProgramLead = false,
  canShowViewDetailButton = true,
  hideStarted = false,
}: ParticipantComponentStatisticProps) {
  const totalParticipants = component?.totalParticipants || 0;
  const statisticData = getStatisticDataFormated(component, hideStarted);

  return (
    <div
      className={cn(
        "xs:p-3.5 xs:gap-3.5 flex flex-1 flex-col md:gap-4 md:p-4",
        CARD_CLASSES,
      )}
      css={{ background: Colors.WHITE }}
    >
      <Flex alignItems={"center"}>
        <EmbedSvg src={participantsIcon} height={26} width={26} />
        <Flex
          alignItems={"center"}
          fontSize={16}
          fontWeight={400}
          color={SUBTEXT_COLOR}
        >
          <Box mx={"5px"} fontSize={32} color={Colors.BLACK} fontWeight={600}>
            {totalParticipants}
          </Box>{" "}
          {pluralize({
            amount: totalParticipants,
            singular: "Participant",
            plural: "Participants",
            showCount: false,
          })}
        </Flex>
      </Flex>
      {totalParticipants !== 0 && (
        <Box flex={1} height={12} width={1} display={["none", "flex"]}>
          {statisticData
            .filter((item) => item.count > 0)
            .map((item, index) => (
              <Flex
                key={item.label}
                flex={item.count}
                justifyContent={"center"}
                css={
                  index !== statisticData.length - 1 ? { marginRight: 5 } : null
                }
              >
                <Box
                  width={1}
                  backgroundColor={item.color}
                  height={12}
                  css={{
                    border: `solid 1px ${item.borderColor}`,
                    borderRadius: 4,
                  }}
                ></Box>
              </Flex>
            ))}
        </Box>
      )}
      <Flex flex={1} flexDirection={["column", "row"]}>
        {statisticData.map((item) => (
          <Box key={item.label} mr={"10px"} justifyContent={"center"}>
            <Box color={SUBTEXT_COLOR}>{item.label}</Box>
            <Flex alignItems={"center"}>
              <Box
                backgroundColor={item.color}
                width={12}
                height={12}
                css={{
                  border: `solid 1px ${item.borderColor}`,
                  borderRadius: 4,
                }}
              ></Box>
              <Box fontSize={18} fontWeight={600} ml={"5px"}>
                {item.count}
              </Box>
            </Flex>
          </Box>
        ))}
      </Flex>
      {canShowViewDetailButton && program && (
        <ViewComponentButton
          programId={program.id}
          component={component}
          isProgramLead={isProgramLead}
        />
      )}
    </div>
  );
}
